/**
 * Members and pending invitations. There is no "create user": an admin cannot set another
 * person's password, so account creation is invitation.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "../lib/api";
import { stamp } from "../lib/format";
import type {
  InviteSummary,
  NewInviteResponse,
  Role,
  UserSummary,
  UsersResponse,
} from "../lib/types";
import { Shell, Sep } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Empty, Failed, Loading, Panel } from "../components/ui";

const ROLES: readonly Role[] = ["owner", "admin", "member", "viewer"] as const;

export function Users(): ReactElement {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [issued, setIssued] = useState<NewInviteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listing = useQuery({
    queryKey: ["users"],
    queryFn: () => get<UsersResponse>("/v1/users"),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["users"] });
  };
  const fail = (cause: unknown) =>
    setError(cause instanceof Error ? cause.message : String(cause));

  const invite = useMutation({
    mutationFn: () =>
      post<NewInviteResponse>("/v1/invites", { email: email.trim(), role }),
    onSuccess: (created) => {
      setIssued(created);
      setEmail("");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const change = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      patch<UserSummary>(`/v1/users/${input.id}`, input.body),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/v1/users/${id}`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const revokeInvite = useMutation({
    mutationFn: (id: string) => del(`/v1/invites/${id}`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const users = listing.data?.users ?? [];
  // Computed client-side so the control is absent rather than failing on click; the
  // server's 409 is the real guard, and a race still lands there.
  const lastOwner = useMemo(() => {
    const owners = users.filter((user) => user.role === "owner");
    return owners.length === 1 ? owners[0]?.id : undefined;
  }, [users]);

  return (
    <Shell crumb={<>settings <Sep /> users</>} fill>
      {error && (
        <p className="border border-crit/40 bg-crit-wash px-3 py-2 text-[12.5px] text-crit">
          {error}
        </p>
      )}

      <Panel title="Invite someone">
        <form
          className="flex flex-wrap items-end gap-3 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            invite.mutate();
          }}
        >
          <div className="min-w-[260px] flex-1">
            <label htmlFor="invite-email" className="eyebrow">
              // invite by email
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-2 w-full border border-hair bg-ground px-3 py-2 font-mono text-[13px] outline-none focus:border-gold"
            />
          </div>
          <div>
            <label htmlFor="invite-role" className="eyebrow">
              // role
            </label>
            <select
              id="invite-role"
              value={role}
              onChange={(event) => setRole(event.target.value as Role)}
              className="mt-2 border border-hair bg-ground px-3 py-2 font-mono text-[13px] outline-none focus:border-gold"
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <Button variant="primary" type="submit" disabled={invite.isPending}>
            send invitation
          </Button>
        </form>

        {issued && (
          <div className="border-t border-hair px-4 py-3">
            <p className="eyebrow">// invitation link</p>
            <code
              data-testid="invite-link"
              className="mt-2 block break-all border border-hair bg-ground px-3 py-2 font-mono text-[12px]"
            >
              {window.location.origin + issued.invite_path}
            </code>
            <div className="mt-2 flex items-center gap-3">
              <Button
                onClick={() => {
                  void navigator.clipboard?.writeText(
                    window.location.origin + issued.invite_path,
                  );
                }}
              >
                copy
              </Button>
              <span className="text-[12px] text-dim">
                This is the only time it is shown. It expires {stamp(issued.expires_at)}.
              </span>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Members" fill>
        {listing.isPending ? (
          <Loading what="members" />
        ) : listing.error ? (
          <Failed error={listing.error} what="members" />
        ) : (
          <DataTable
            fill
            table="users"
            rows={users}
            rowKey={(user) => user.id}
            columns={[
              { key: "name", header: "Name", width: 200, render: (u) => u.display_name },
              {
                key: "email",
                header: "Email",
                width: 260,
                cellClass: "font-mono",
                render: (u) => u.email,
              },
              {
                key: "role",
                header: "Role",
                width: 140,
                render: (u) => (
                  <select
                    aria-label={`role for ${u.email}`}
                    value={u.role}
                    onChange={(event) =>
                      change.mutate({ id: u.id, body: { role: event.target.value } })
                    }
                    className="w-full border border-hair bg-ground px-2 py-1 font-mono text-[12px]"
                  >
                    {ROLES.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                key: "created",
                header: "Created",
                width: 170,
                cellClass: "font-mono",
                render: (u) => stamp(u.created_at),
              },
              {
                key: "state",
                header: "State",
                width: 120,
                render: (u) => (u.disabled_at ? "disabled" : "active"),
              },
              {
                key: "actions",
                header: "",
                width: 190,
                align: "end",
                render: (u) =>
                  u.id === lastOwner ? (
                    <span className="font-mono text-[10.5px] text-faint">last owner</span>
                  ) : (
                    <span className="flex justify-end gap-2">
                      <Button
                        data-testid={`disable-${u.id}`}
                        onClick={() =>
                          change.mutate({
                            id: u.id,
                            body: { disabled: !u.disabled_at },
                          })
                        }
                      >
                        {u.disabled_at ? "enable" : "disable"}
                      </Button>
                      <Button
                        data-testid={`delete-${u.id}`}
                        onClick={() => remove.mutate(u.id)}
                      >
                        delete
                      </Button>
                    </span>
                  ),
              },
            ]}
          />
        )}
      </Panel>

      <Panel title="Pending invitations">
        {(listing.data?.invites.length ?? 0) === 0 ? (
          <Empty title="No invitations" hint="Invite someone above; the link is copyable." />
        ) : (
          <DataTable
            table="invites"
            rows={listing.data?.invites ?? []}
            rowKey={(item: InviteSummary) => item.id}
            columns={[
              {
                key: "email",
                header: "Email",
                width: 280,
                cellClass: "font-mono",
                render: (i) => i.email,
              },
              { key: "role", header: "Role", width: 120, render: (i) => i.role },
              {
                key: "expires",
                header: "Expires",
                width: 180,
                cellClass: "font-mono",
                render: (i) => stamp(i.expires_at),
              },
              {
                key: "by",
                header: "Invited by",
                width: 240,
                cellClass: "font-mono",
                render: (i) => i.created_by,
              },
              {
                key: "actions",
                header: "",
                width: 110,
                align: "end",
                render: (i) => (
                  <Button onClick={() => revokeInvite.mutate(i.id)}>revoke</Button>
                ),
              },
            ]}
          />
        )}
      </Panel>
    </Shell>
  );
}
